import { Typography } from '@euler/app/components/typography';
import { WiredDashboardTaskList } from '@euler/app/tv/components/dashboard/TaskList';
import { TaskListTabInfo } from '@euler/app/tv/components/dashboard/TaskListToolbar';
import { LicensePlateNoPanel } from '@euler/app/tv/components/search/LicensePlateNoPanel';
import { SearchCriteriaBookmarksPanel } from '@euler/app/tv/components/search/SearchCriteriaBookmarksPanel';
import { SearchCriteriaContainer } from '@euler/app/tv/components/search/SearchCriteriaContainer';
import {
  SearchCriteriaContextProvider,
  useSearchCriteriaContext,
} from '@euler/app/tv/functions/SearchCriteriaContext';
import { isSearchParamsValid } from '@euler/app/tv/functions/search-criteria/utils';
import { useTaskListWithSearch } from '@euler/app/tv/functions/useTaskListWithSearch';
import { useScreenContentPadding } from '@euler/app/tv/functions/utils';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { InspectionTaskStatus } from '@euler/model/enum';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { memo, useEffect, useMemo } from 'react';
import { View } from 'react-native';

export const SearchScreen = wrapNavigatorScreen(() => {
  return (
    <SearchCriteriaContextProvider>
      <Content />
    </SearchCriteriaContextProvider>
  );
});

const Content = memo(() => {
  const { rpx } = useResponsiveContext();

  const [paddingLeft, paddingRight] = useScreenContentPadding();

  const { searchParams$, query$ } = useSearchCriteriaContext();

  const [searchParams] = useBehaviorSubject(searchParams$);

  const toSearchParams = usePersistFn(() => searchParams ?? {});

  const tabItems = useMemo<TaskListTabInfo[]>(
    () => [
      {
        type: 'search',
        text: '搜索结果',
        toSearchParams,
      },
    ],
    [toSearchParams],
  );

  const { onRefresh, ...taskList } = useTaskListWithSearch({
    tabItems,
    initialTabType: 'search',
    defaultLimit: 10,
    query$,
    debounce: 1000,
    //shouldPerformSearch: isSearchParamsValid,
  });

  const theme = usePresentationTheme();

  useEffect(() => {
    if (searchParams != null) {
      onRefresh();
    }
  }, [searchParams, onRefresh]);

  return (
    <View
      css={`
        flex: 1;
      `}
    >
      <HStack
        alignItems="stretch"
        css={`
          padding: ${rpx(40)}px ${paddingRight}px ${rpx(40)}px ${paddingLeft}px;
        `}
      >
        <LicensePlateNoPanel
          onSearch={onRefresh}
          css={`
            width: 35%;
          `}
        />
        <SearchCriteriaContainer
          css={`
            margin-left: ${rpx(40)}px;
            flex: 1;
          `}
        />
        <SearchCriteriaBookmarksPanel
          css={`
            margin-left: ${rpx(40)}px;
            width: 25%;
          `}
        />
      </HStack>
      <View
        css={`
          flex: 1;
        `}
      >
        <HStack
          css={`
            margin-bottom: ${rpx(10)}px;
          `}
        >
          <Typography.Label
            bold
            size={rpx(32)}
            lineHeight={rpx(48)}
            color={theme.panel.titleTextColor}
            css={`
              margin-left: ${paddingLeft}px;
            `}
          >
            搜索结果
          </Typography.Label>
          <View
            css={`
              height: 1px;
              flex: 1;
              margin-left: ${rpx(20)}px;
              margin-right: ${paddingRight}px;
              background-color: ${theme.search.separatorLineColor};
            `}
          />
        </HStack>
        <WiredDashboardTaskList
          debugLabel="search"
          tasks={taskList.data}
          preferredMode={taskList.preferredMode}
          maxTaskPerPack={1}
          showStatus={true}
          showFinishedTime={
            searchParams?.status === InspectionTaskStatus.Finished
          }
          isRefreshing={taskList.isRefreshing}
          isLoading={taskList.isLoading}
          hasMore={taskList.hasMore}
          error={taskList.error}
          emptyText={
            searchParams != null && isSearchParamsValid(searchParams)
              ? '暂无相关搜索结果'
              : '输入车牌，或修改搜索条件以进行搜索'
          }
          emptyErrorMessage={'搜索服务订单时发生了错误，请稍候重试!'}
          emptyIconSize={rpx(120)}
          emptyTextStyle={{
            fontSize: rpx(23),
            marginTop: rpx(10),
          }}
          emptyButtonStyle={{
            marginTop: 0,
          }}
          emptyButtonTextStyle={{
            fontSize: rpx(24),
            lineHeight: rpx(32),
          }}
          onRefresh={onRefresh}
          onFetchMore={taskList.onFetchMore}
        />
      </View>
    </View>
  );
});
